import { supabase } from '../supabase';
import type { LiveEvent } from '../live';

const mapEvent = (r: any): LiveEvent => ({
    id: Number(r.id),
    at: new Date(r.at).getTime(),
    type: r.type,
    classId: r.class_id || undefined,
    studentId: r.student_id || undefined,
    productId: r.product_id || undefined,
    orderId: r.order_id || undefined,
    amount: r.amount === null || r.amount === undefined ? undefined : Number(r.amount),
    detail: r.detail || null,
});

const COLUMNS = 'id, at, type, class_id, student_id, product_id, order_id, amount, detail';

/**
 * Admin: händelserna från och med en tidpunkt, senaste först. Kastar om
 * tabellen saknas (migreringen inte körd), så att vyn kan säga det.
 */
export const getLiveEvents = async (sinceIso: string, limit = 500): Promise<LiveEvent[]> => {
    const { data, error } = await supabase
        .from('live_events')
        .select(COLUMNS)
        .gte('at', sinceIso)
        .order('at', { ascending: false })
        .limit(limit);
    if (error) throw error;
    return ((data as any[]) || []).map(mapEvent);
};

export type LiveStatus = 'connecting' | 'live' | 'offline';

/**
 * Admin: nya händelser direkt när de sparas (Supabase Realtime). Databasen
 * skickar bara rader som admin får läsa. Returnerar en funktion som kopplar ner.
 */
export const subscribeLiveEvents = (
    onEvent: (event: LiveEvent) => void,
    onStatus: (status: LiveStatus) => void
): (() => void) => {
    let closed = false;
    const channel = supabase.channel('admin-live-events');
    channel.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'live_events' }, payload => {
        if (payload.new) onEvent(mapEvent(payload.new));
    });
    // Inloggningens token till Realtime innan anslutningen, så att admin-regeln gäller.
    Promise.resolve(supabase.realtime.setAuth())
        .catch(() => { /* tokenen sätts även vid inloggning */ })
        .then(() => {
            if (closed) return;
            channel.subscribe(status => {
                if (status === 'SUBSCRIBED') onStatus('live');
                else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') onStatus(closed ? 'offline' : 'connecting');
            });
        });
    return () => {
        closed = true;
        supabase.removeChannel(channel);
    };
};
